/**
 * Canonical Mobile Bottom Navigation
 * Single source of truth for the mobile bottom tab bar across the public site.
 * Rendered by PublicLayout and PublicMapLayout only (never mounted twice).
 *
 * Tabs: Home · Collection · Arena · Tickets · Research
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Archive, Map, Ticket, BookOpen } from 'lucide-react';

const NAV_ITEMS = [
  { id: 'home', label: 'Home', to: '/', icon: Home },
  { id: 'collection', label: 'Collection', to: '/collection', icon: Archive },
  { id: 'arena', label: 'Arena', to: '/ticket-registry/map', icon: Map },
  { id: 'tickets', label: 'Tickets', to: '/ticket-registry/list', icon: Ticket },
  { id: 'research', label: 'Research', to: '/research', icon: BookOpen },
];

const MobileBottomNav = () => {
  const { pathname } = useLocation();

  const isActive = (item) => {
    switch (item.id) {
      case 'home':
        return pathname === '/';
      case 'arena':
        return pathname === '/ticket-registry/map' || pathname === '/arena' || pathname === '/seat-map';
      case 'tickets':
        return (
          pathname.startsWith('/ticket-registry/list') ||
          pathname.startsWith('/ticket-registry/tickets') ||
          pathname === '/tickets'
        );
      case 'collection':
        return pathname.startsWith('/collection') || pathname.startsWith('/artifacts');
      case 'research':
        return pathname.startsWith('/research') || pathname.startsWith('/studies') || pathname.startsWith('/facts');
      default:
        return false;
    }
  };

  return (
    <nav className="canonical-mobile-nav" aria-label="Primary" data-testid="mobile-bottom-nav">
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const active = isActive(item);
        return (
          <Link
            key={item.id}
            to={item.to}
            className={`canonical-mobile-nav__item${active ? ' is-active' : ''}`}
            aria-current={active ? 'page' : undefined}
            data-testid={`mobile-nav-${item.id}`}
          >
            <Icon size={22} strokeWidth={active ? 2.4 : 1.9} />
            <span>{item.label}</span>
          </Link>
        );
      })}

      <style>{`
        .canonical-mobile-nav { display: none; }

        @media (max-width: 900px) {
          .canonical-mobile-nav {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 120;
            min-height: calc(60px + env(safe-area-inset-bottom, 0px));
            padding-bottom: env(safe-area-inset-bottom, 0px);
            background: #F7F1E5;
            border-top: 1px solid rgba(181, 138, 60, 0.35);
            box-shadow: 0 -8px 24px rgba(11, 45, 69, 0.10);
          }
        }

        .canonical-mobile-nav__item {
          min-width: 0;
          min-height: 60px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;
          padding: 8px 4px;
          color: #0D273C;
          text-decoration: none;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          transition: color 0.15s;
        }

        .canonical-mobile-nav__item.is-active { color: #D85C4A; }
        .canonical-mobile-nav__item:active { background: rgba(0, 0, 0, 0.04); }
        .canonical-mobile-nav__item:focus-visible { outline: 2px solid #D85C4A; outline-offset: -4px; }
      `}</style>
    </nav>
  );
};

export default MobileBottomNav;
